import React, { useState, useRef, useEffect, useMemo } from 'react';
import './DevicePicker.css';

// Display name precedence: the machine label wins, because that is what an
// operator recognises on the floor. The bound name is the fallback for machines
// that have no label yet, then the factory machine_id, then the ThingsBoard
// name (a MAC string, and the reason this picker exists).
export const deviceTitle = (d) =>
  (d && (d.machine_label || d.device_name || d.machine_id || d.thingsboard_device_id)) || 'Unknown device';

// Secondary line: whatever adds identity without repeating the title. Two
// machines really can share a label ("JIG BOT 1" x2), so the ThingsBoard name
// is always shown when it is not already the title.
const deviceSubtitle = (d) => {
  const bits = [];
  if (d.machine_id && d.machine_id !== deviceTitle(d)) bits.push(d.machine_id);
  if (d.device_name && d.device_name !== deviceTitle(d)) bits.push(d.device_name);
  return bits.join(' · ');
};

// The gateway auto-generates ThingsBoard names as <product>_<MAC>, e.g.
// aquasew_08B61F34AD94. That is not a name a person chose, so it must not count
// as named. (The previous test compared device_name against the device UUID,
// which is always true, so every unnamed machine passed and the sort below
// never sank any of them.)
const AUTO_NAME = /^[a-z0-9]+_[0-9a-f]{12}$/i;
const isNamed = (d) => !!(d.machine_label || (d.device_name && !AUTO_NAME.test(d.device_name)));

// A binding whose ThingsBoard device is gone. Kept out of Online/Offline
// because "offline" is a plausible state and hides the problem: a deleted
// device looked exactly like a machine someone had switched off.
const isBroken = (d) => d.binding === 'missing';

// An offline row is read to answer one question: when did this machine stop?
// For a machine that stopped today the clock time is the useful half; for an
// older one the date is. Showing both always is wider than a phone row affords.
//
// Formatted by hand rather than through toLocaleString: the browser locale
// decides field order there, so 10/09/2026 renders as 09/10/2026 on a
// US-locale phone -- the same string meaning a different day.
const pad = (n) => String(n).padStart(2, '0');

const clockTime = (t) => {
  const h = t.getHours();
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${pad(t.getMinutes())}${h < 12 ? 'am' : 'pm'}`;
};

// null whenever there is nothing trustworthy to show -- a machine that has
// never reported, or a binding whose device is gone -- so the caller renders
// nothing rather than a placeholder.
const lastActiveText = (d) => {
  if (!d || !d.last_activity) return null;
  const t = new Date(Number(d.last_activity));
  if (Number.isNaN(t.getTime())) return null;
  const now = new Date();
  const sameDay = t.getDate() === now.getDate()
    && t.getMonth() === now.getMonth()
    && t.getFullYear() === now.getFullYear();
  return sameDay
    ? clockTime(t)
    : `${pad(t.getDate())}/${pad(t.getMonth() + 1)}/${t.getFullYear()}`;
};

const DevicePicker = ({ devices, selectedDevice, onSelect }) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [highlight, setHighlight] = useState(0);
  const rootRef = useRef(null);
  const searchRef = useRef(null);
  const panelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDocClick = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) {
      // Focusing the search box on open pops the on-screen keyboard the instant
      // the menu appears, covering half the machine list for anyone who only
      // wants to pick from it. On a touch device focus the panel instead -- the
      // keyboard then appears only when the search box is actually tapped.
      // A mouse still lands in the search box, so typing to filter is unchanged.
      const coarsePointer = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
      const target = coarsePointer ? panelRef.current : searchRef.current;
      if (target) target.focus();
    }
    if (!open) { setQuery(''); setHighlight(0); }
  }, [open]);

  // search matches the label, the machine_id and the ThingsBoard name, so a
  // machine can be found by what it is called, its asset number, or its MAC
  const matches = (d) => {
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return [d.machine_label, d.device_name, d.machine_id, d.thingsboard_device_id]
      .filter(Boolean)
      .some((v) => String(v).toLowerCase().includes(q));
  };

  const groups = useMemo(() => {
    const byName = (a, b) => {
      // unnamed machines sink to the bottom of their group so they are obvious
      // and get named, rather than hiding under "a" for aquasew_...
      if (isNamed(a) !== isNamed(b)) return isNamed(a) ? -1 : 1;
      return deviceTitle(a).localeCompare(deviceTitle(b), undefined, { sensitivity: 'base' });
    };
    const shown = (devices || []).filter(matches);
    return [
      { label: 'Online', items: shown.filter((d) => d.active && !isBroken(d)).sort(byName) },
      { label: 'Offline', items: shown.filter((d) => !d.active && !isBroken(d)).sort(byName) },
      { label: 'Needs attention', items: shown.filter(isBroken).sort(byName) }
    ].filter((g) => g.items.length > 0);
  }, [devices, query]); // eslint-disable-line react-hooks/exhaustive-deps

  const flat = groups.reduce((acc, g) => acc.concat(g.items), []);

  const choose = (d) => { onSelect(d); setOpen(false); };

  const onKeyDown = (e) => {
    if (e.key === 'Escape') { setOpen(false); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setHighlight((h) => Math.min(h + 1, flat.length - 1)); }
    if (e.key === 'ArrowUp') { e.preventDefault(); setHighlight((h) => Math.max(h - 1, 0)); }
    if (e.key === 'Enter' && flat[highlight]) { e.preventDefault(); choose(flat[highlight]); }
  };

  return (
    <div className="device-picker" ref={rootRef}>
      <button
        type="button"
        className="dp-trigger"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <span className={`dp-dot ${selectedDevice && selectedDevice.active ? 'on' : 'off'}`} />
        <span className="dp-trigger-text">
          <span className="dp-trigger-title">
            <span className="dp-machine-name">
              {selectedDevice ? deviceTitle(selectedDevice) : 'Select a machine'}
            </span>
            {selectedDevice && !selectedDevice.active && lastActiveText(selectedDevice) && (
              <span className="dp-last" title="Last time this machine reported">
                {lastActiveText(selectedDevice)}
              </span>
            )}
          </span>
          {selectedDevice && deviceSubtitle(selectedDevice) && (
            <span className="dp-trigger-sub">{deviceSubtitle(selectedDevice)}</span>
          )}
        </span>
        <span className="dp-caret">▾</span>
      </button>

      {open && (
        <div className="dp-panel" role="listbox" ref={panelRef} tabIndex={-1} onKeyDown={onKeyDown}>
          <input
            ref={searchRef}
            className="dp-search"
            type="text"
            value={query}
            placeholder="Search name, machine id or MAC…"
            onChange={(e) => { setQuery(e.target.value); setHighlight(0); }}
            onKeyDown={onKeyDown}
          />
          <div className="dp-list">
            {flat.length === 0 && <div className="dp-empty">No machine matches “{query}”</div>}
            {groups.map((g) => (
              <div key={g.label} className="dp-group">
                <div className="dp-group-label">{g.label} · {g.items.length}</div>
                {g.items.map((d) => {
                  const idx = flat.indexOf(d);
                  const sel = selectedDevice && selectedDevice.id === d.id;
                  return (
                    <button
                      type="button"
                      key={d.id}
                      role="option"
                      aria-selected={sel}
                      className={`dp-item ${idx === highlight ? 'hl' : ''} ${sel ? 'sel' : ''}`}
                      onMouseEnter={() => setHighlight(idx)}
                      onClick={() => choose(d)}
                    >
                      <span className={`dp-dot ${d.active ? 'on' : 'off'}`} />
                      <span className="dp-item-text">
                        <span className="dp-item-title">
                          <span className="dp-machine-name">{deviceTitle(d)}</span>
                          {!d.active && lastActiveText(d) && (
                            <span className="dp-last" title="Last time this machine reported">
                              {lastActiveText(d)}
                            </span>
                          )}
                          {d.is_default && <span className="dp-default">default</span>}
                          {isBroken(d) && (
                            <span className="dp-broken" title="This machine is no longer registered. An administrator can repair or remove it.">
                              not registered
                            </span>
                          )}
                        </span>
                        {deviceSubtitle(d) && <span className="dp-item-sub">{deviceSubtitle(d)}</span>}
                      </span>
                    </button>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DevicePicker;
